import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function SectionBgImage({ config = {}, mediaFolders = {} }) {
    const imgRef = useRef(null);

    // Resolve image
    let url = config.bg_image_url;
    let variantUrls = null;
    let imgWidth = null;
    let imgHeight = null;
    if (config.bg_image_folder_id && config.bg_image_file_id && mediaFolders[config.bg_image_folder_id]) {
        const found = mediaFolders[config.bg_image_folder_id].find(f => String(f.id) === String(config.bg_image_file_id));
        if (found) {
            url = found.image_url;
            variantUrls = found.variant_urls;
            imgWidth = found.width;
            imgHeight = found.height;
        }
    }

    // Build srcSet from variant_urls for responsive loading
    let srcSet = undefined;
    if (variantUrls) {
        const entries = [];
        for (const [key, val] of Object.entries(variantUrls)) {
            if (val) entries.push(`${val} ${key}`);
        }
        if (url && entries.length) entries.push(`${url} 2400w`);
        if (entries.length) srcSet = entries.join(', ');
    }

    useEffect(() => {
        if (!url || !imgRef.current) return;

        // Skip parallax inside iframes (editor preview)
        try { if (window.self !== window.top) return; } catch (e) { return; }

        // Respect prefers-reduced-motion
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

        let ctx;
        try {
            ctx = gsap.context(() => {
                gsap.fromTo(imgRef.current,
                    { y: -30 },
                    {
                        y: 30,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: imgRef.current.closest('section') || imgRef.current.parentElement,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true,
                        },
                    }
                );
            });
        } catch (e) {
            // GSAP/ScrollTrigger failed — image displays normally without parallax
        }

        return () => { try { ctx?.revert(); } catch (e) {} };
    }, [url]);

    if (!url) return null;
    const overlay = (config.bg_image_overlay ?? 50) / 100;
    return (
        <>
            <img
                ref={imgRef}
                src={url}
                srcSet={srcSet}
                sizes="100vw"
                loading="lazy"
                decoding="async"
                width={imgWidth || undefined}
                height={imgHeight || undefined}
                className="absolute -top-[10%] left-0 w-full h-[120%] object-cover z-0"
                alt=""
            />
            <div className="absolute inset-0 z-0" style={{ backgroundColor: `rgba(0,0,0,${overlay})` }} />
        </>
    );
}
